import axiosInstance from "./axiosConfig";
import { APIError, handleAPIError } from "./utils/errors";
import {
  UrlToPdfFormat,
  UrlToPdfRequest,
  UrlToPdfResult,
  UploadByLinkRequest,
  UploadByLinkResult
} from "./types/file";
import {
  decodeBase64ToBlob,
  extractFilenameFromContentDisposition
} from "./utils/file";

export async function urlToPdf(
  request: UrlToPdfRequest,
  format: UrlToPdfFormat = "binary"
): Promise<UrlToPdfResult> {
  const { url, topic_id, case_id } = request;
  if (!url || typeof url !== "string") {
    throw new APIError("A valid URL string is required", 400);
  }

  try {
    if (format === "json") {
      const data: any = { url };
      if (topic_id) data.topic_id = topic_id;
      if (case_id) data.case_id = case_id;
      const response = await axiosInstance.post<{
        fileName: string;
        data: string;
        sourceUrl?: string;
      }>("/url_to_pdf", data, { params: { format: "json" } });

      const fileName = response.data.fileName || "document.pdf";
      const blob = decodeBase64ToBlob(response.data.data, "application/pdf");
      const { sourceUrl } = response.data;

      return { fileName, blob, sourceUrl };
    }

    const binaryData: any = { url };
    if (topic_id) binaryData.topic_id = topic_id;
    if (case_id) binaryData.case_id = case_id;
    const response = await axiosInstance.post("/url_to_pdf", binaryData, {
      responseType: "arraybuffer",
      params: { format: "binary" }
    });

    const contentDisposition: string | undefined =
      response.headers["content-disposition"];
    const contentType: string =
      response.headers["content-type"] || "application/pdf";
    const sourceUrl: string | undefined = response.headers["x-source-url"];

    const fileName =
      extractFilenameFromContentDisposition(contentDisposition) ||
      "document.pdf";
    const blob = new Blob([response.data as ArrayBuffer], {
      type: contentType
    });

    return { fileName, blob, sourceUrl };
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
}

export async function uploadByExternalLink(
  request: UploadByLinkRequest
): Promise<UploadByLinkResult> {
  const { url, topic_id, case_id } = request;
  if (!url || typeof url !== "string") {
    throw new APIError("A valid URL string is required", 400);
  }

  try {
    const storageData: any = { url };
    if (topic_id) storageData.topic_id = topic_id;
    if (case_id) storageData.case_id = case_id;
    const response = await axiosInstance.post<{ file_path: string }>(
      "/url_to_pdf",
      storageData,
      { params: { format: "storage-path" } }
    );

    const filePath = response.data.file_path;
    if (!filePath) {
      throw new APIError("Missing file_path in response", 500);
    }
    return { filePath };
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
}

export const FileAPI = {
  urlToPdf,
  uploadByExternalLink
};

export default FileAPI;
